// Short-lived, account-scoped read cache. Never cache room membership, game
// commands or authorization decisions. Clearing also detaches pending reads so
// a late response cannot resurrect data invalidated by a save or sign-out.
type Entry = { promise: Promise<unknown>; expiresAt: number; pending: boolean };
const entries = new Map<string, Entry>();
let account: string | null = null;
const MAX_ENTRIES = 100;

export function clearAccountRequests() {
  entries.clear();
  account = null;
}

function selectAccount(uid: string | null) {
  if (account !== uid) {
    entries.clear();
    account = uid;
  }
}

export function invalidateAccountRequests(uid: string | null, ...keys: string[]) {
  // A mutation belonging to an old session must not evict the new user's data.
  if (account !== uid) return;
  for (const key of keys) entries.delete(key);
}

export function cachedAccountRequest<T>(uid: string | null, key: string, load: () => Promise<T>, force = false, ttl = 30_000): Promise<T> {
  selectAccount(uid);
  if (!uid) return load();
  const existing = entries.get(key);
  if (existing && (existing.pending || (!force && existing.expiresAt > Date.now()))) {
    return existing.promise as Promise<T>;
  }
  const entry: Entry = { promise: Promise.resolve(), expiresAt: 0, pending: true };
  entry.promise = Promise.resolve().then(load).then((result) => {
    entry.pending = false;
    entry.expiresAt = Date.now() + ttl;
    return result;
  }, (error: unknown) => {
    if (entries.get(key) === entry) entries.delete(key);
    throw error;
  });
  entries.delete(key);
  entries.set(key, entry);
  if (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
  return entry.promise as Promise<T>;
}
